import { useState } from 'react';
import Button from '../../ui/Button';
import { Dialog } from './AccountBits';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { money } from '../../../utils/nurseryMoney';
import { CREDIT_REASON_LABELS, type CreditReason } from '../../../types/nurseryAccounts';

/**
 * Accept what an overdue invoice was paid and write off the rest. The server
 * raises one credit note for the remaining balance, which closes the invoice.
 * Shared by the invoice page and the Short payments tab.
 */

const REASONS: CreditReason[] = [
  'short_payment', 'short_delivery', 'damaged', 'price_correction', 'settlement_discount', 'other',
];

export interface AcceptShortPaymentDialogProps {
  invoice: { id: number; invoice_number: string; balance: number | string };
  onClose: () => void;
  /** Called once the credit note has been raised. */
  onDone: () => void;
}

export function AcceptShortPaymentDialog({ invoice, onClose, onDone }: AcceptShortPaymentDialogProps) {
  const [reason, setReason] = useState<CreditReason>('short_payment');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = note.trim().length >= 3;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.acceptShortPayment(invoice.id, { reason, note: note.trim() });
      if (res.success) {
        onDone();
        return;
      }
      setError(errorMessage(res, 'Could not accept the short payment'));
    } catch (e) {
      setError(errorMessage(e, 'Could not accept the short payment'));
    }
    setBusy(false);
  };

  return (
    <Dialog
      title="Accept short payment"
      subtitle={`Invoice ${invoice.invoice_number}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!ok || busy}>
            {busy ? 'Working…' : `Credit ${money(invoice.balance)} and close`}
          </Button>
        </>
      }
    >
      <p className="text-sm text-neutral-700 break-words">
        A credit note for the remaining <span className="font-semibold">{money(invoice.balance)}</span> will be raised
        against this invoice, which closes it. Nothing more will be expected from the customer for it.
      </p>
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
      <div>
        <label htmlFor="short-pay-reason" className="block text-sm font-medium text-neutral-700 mb-1">Why it was paid short</label>
        <select id="short-pay-reason" value={reason} onChange={(e) => setReason(e.target.value as CreditReason)}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
          {REASONS.map((r) => <option key={r} value={r}>{CREDIT_REASON_LABELS[r]}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="short-pay-note" className="block text-sm font-medium text-neutral-700 mb-1">
          What happened <span className="text-red-500">*</span>
        </label>
        <textarea id="short-pay-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="For example: 12 trees arrived damaged, agreed with the store manager."
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
        <p className="text-xs text-neutral-500 mt-1">This is kept on the credit note.</p>
      </div>
    </Dialog>
  );
}

export default AcceptShortPaymentDialog;
